/*
AddCustomWord.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import { useState, useRef, lazy } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useTranslation } from 'react-i18next'
const Card = lazy(()=> import('../../components/Card')) 
import XrxInput from '../../components/xrx-input'
import PopoverRow from '../../components/popoverRow'
import { DropdownOption } from '../../components/scrollableDropdown'
import { useRedactionContext } from '../../providers/redactionProvider'
import { ProfileCustomField } from '../../modules/profileCustomField'
import { ScrollWrapperRef } from '../../components/scrollWrapper'
import ScrollWrapperLoader from '../../components/scrollWrapperLoader'
import { useBaseContext } from '../../providers/baseProvider'

export default function AddCustomWord() {
  const navigate = useNavigate()
  const { index } = useParams<{ index: string }>()
  const isEditing = index !== undefined
  const baseContext = useBaseContext();
  const redactionContext = useRedactionContext()
  const { draftRedactProfile, setDraftRedactProfile } = redactionContext
  const existingField = isEditing ? draftRedactProfile?.customFields[parseInt(index)] : undefined
  const [customField, setCustomField] = useState<ProfileCustomField>(
    existingField ?? {
      text: '',
      deviceGuid: baseContext.deviceId ?? "",
      contains: true
    }
  )
  const [isValid, setIsValid] = useState<boolean | undefined>()
  const [validationMessages, setValidationMessages] = useState<string[]>([])
  const { t } = useTranslation()
  const customWordRequiredError = t('ERROR.ENTER_CUSTOM_WORD_OR_PHRASE')
  const matchCriteriaScrollRef = useRef<ScrollWrapperRef>(null)

  const matchCriteriaOptions: DropdownOption[] = [
    {
      name: 'CONTAINS',
      translationKey: 'CONTAINS',
      value: 'CONTAINS'
    },
    {
      name: 'MATCHES',
      translationKey: 'MATCHES',
      value: 'MATCHES'
    }
  ]

  const handleCancel = () => {
    navigate(-1)
  }
  const handleCustomWordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setCustomField({ ...customField, text: value })

    if (
      isValid === false &&
      validationMessages.includes(customWordRequiredError) &&
      value.trim() !== ''
    ) {
      setIsValid(undefined)
      setValidationMessages([])
    }
  }

  const handleOk = () => {
    if (!customField.text || customField.text.trim() === '') {
      setIsValid(false)
      setValidationMessages([customWordRequiredError])
      return
    }
    setDraftRedactProfile(prevState => {
      if (prevState) {
        if (isEditing) {
          const updatedFields = [...prevState.customFields]
          updatedFields[parseInt(index!)] = customField
          return { ...prevState, customFields: updatedFields }
        }
        return { 
          ...prevState, 
          customFields: [...prevState.customFields, customField] 
        }
      }
      return prevState
    })  
    navigate(-1)
  }

  return (
      <Card
        title={isEditing ? t('EDIT_CUSTOM') : t('ADD_CUSTOM')}
        onOk={handleOk}
        onCancel={handleCancel}
        XPadding='none'
        contentContainerClass='pt-3'
      >
        <div className='row justify-content-center mx-3_75'>
          <div className='col-12 px-0'>
            <XrxInput
              id='custom-word'
              inputType='clearable'
              placeholder=''
              label={t('CUSTOM_WORD_OR_PHRASE')}
              value={customField.text}
              onChange={handleCustomWordChange}
              ariaLabel={t('CUSTOM_WORD_OR_PHRASE')}
              onClear={() => setCustomField({ ...customField, text: '' })}
              autoFocus
              valid={isValid}
              validationMessages={validationMessages}
            />
          </div>
        </div>
                <div className='row mx-0'>
          <div className="col-12 px-0">
              <div id='match-criteria-conatiner' className=''>
                <ScrollWrapperLoader ref={matchCriteriaScrollRef} scrollId='match-criteria-scroller'>

            <ul className='xrx-list-group list-group-flush bg-white'>
              <PopoverRow
                title='MATCH_CRITERIA'
                iconPath="/img/icons/icon-match-criteria.svg"
                iconStyles="ml-4 mr-0"
                value={customField.contains ? matchCriteriaOptions[0] : matchCriteriaOptions[1]}
                options={matchCriteriaOptions}
                defaultOption={customField.contains ? matchCriteriaOptions[0] : matchCriteriaOptions[1]}
                onSelect={(option: DropdownOption) => setCustomField({ ...customField, contains: option.value === 'CONTAINS' })}
                id='match-criteria'
              />
            </ul>

        </ScrollWrapperLoader>
        </div>
        </div>
                  </div>
      </Card>
  )
}
